import { z } from 'zod';
import { $NaturalNumber, i18nAttributes, isoDateString, language, uuid } from '../../utils';
import { RedocSchemaObject, extendApi } from '../../openapi';
import { publicCustomAttribute } from './custom-attribute';
import { publicLevelRef } from './level';

export const publicProfile = extendApi(
  z.object({
    id: uuid,
    firstName: extendApi(z.string(), { description: 'First name of the member', example: 'John' }),
    lastName: extendApi(z.string(), { description: 'Last name of the member', example: 'Doe' }),
    profileVersion: extendApi(z.number(), {
      description: 'Latest version of the profile',
      example: 5,
    }),
    identities: extendApi(z.record(z.string().min(1), z.string().min(1)), {
      additionalProperties: {
        'x-additionalPropertiesName': 'identity key',
        type: 'string',
        minLength: 1,
        description: 'Value of the identity for this member and program',
      },
      example: {
        email: 'john.doe@example.com',
        customer_id: '123456',
      },
    } as RedocSchemaObject),
    membership: extendApi(
      z
        .object({
          id: uuid,
        })
        .optional(),
      {
        description: 'Membership for which the event has been issued',
      },
    ),
    language: extendApi(language, { description: 'Preferred language of the member' }),
    level: extendApi(publicLevelRef.optional(), {
      description:
        'Current level of the member (if the event is of type `level_changed`, then this is the new level)', // only displayed in OAS 3.1
    }),
    lastVisit: extendApi(isoDateString.optional(), {
      description: 'Last time the member visited the portal',
    }),
    currencies: extendApi(
      z.array(
        z.object({
          id: uuid,
          i18nAttributes: i18nAttributes(
            extendApi(
              z.object({
                name: extendApi(z.string(), { description: 'Name of the currency' }),
              }),
              {
                examples: [{ name: 'Coins' }, { name: 'Pièces' }],
              },
            ),
          ),
          value: extendApi(z.number().int(), {
            description: 'Amount of currency the member possesses',
            example: 600,
          }),
          totalAccumulated: extendApi($NaturalNumber, {
            description:
              'Amount of currency the member has earned or been gifted (as if they never had burned or lost points)',
            example: 700,
          }),
          totalOverCurrencyPeriod: extendApi($NaturalNumber, {
            description:
              'Amount of currency the member has earned over the current currency expiration period',
            example: 700,
          }),
        }),
      ),
      {
        description: 'Amounts the member has in each currency',
      },
    ),
    customAttributes: extendApi(
      z.array(
        publicCustomAttribute.extend({
          value: extendApi(z.union([z.string(), isoDateString, z.number(), z.boolean()]), {
            description: 'The type of this property depends on the `type` property',
            example: true,
          }),
        }),
      ),
      {
        description:
          'Custom attributes configured in the Manager and provided by your SSO for this member',
      },
    ),
  }),
  {
    description: 'Profile of the member',
    tags: ['profile-model', 'profile', 'model'],
  } as RedocSchemaObject,
);

export const publicProfileRef = extendApi(publicProfile, {
  $ref: '#/components/schemas/Profile',
} as RedocSchemaObject);

export type PublicProfile = z.infer<typeof publicProfile>;
